import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Search, 
  Bell, 
  User, 
  Settings, 
  Home, 
  Palette, 
  Image, 
  BarChart3, 
  Users, 
  CreditCard,
  HelpCircle,
  ChevronDown,
  Lock,
  Menu,
  X
} from 'lucide-react';

interface PlatformLayoutProps {
  children: React.ReactNode;
}

const sidebarItems = [
  { icon: Home, label: 'Dashboard', active: false, disabled: true },
  { icon: Palette, label: 'Pattern Library', active: true, disabled: false },
  { icon: Image, label: 'My Projects', active: false, disabled: true },
  { icon: BarChart3, label: 'Analytics', active: false, disabled: true },
  { icon: Users, label: 'Team', active: false, disabled: true },
  { icon: Settings, label: 'Settings', active: false, disabled: true },
];

export function PlatformLayout({ children }: PlatformLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            {/* Logo placeholder */}
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.svg" 
                alt="Trilo" 
                className="h-8 w-8"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = `
                    <div class="h-8 w-8 bg-primary rounded flex items-center justify-center">
                      <span class="text-white font-bold text-sm">T</span>
                    </div>
                  `;
                }}
              />
              <Badge variant="secondary" className="ml-2 hidden sm:block">Limited Demo Preview</Badge>
            </div>
            
            {/* Search - Hidden on mobile */}
            <div className="relative disabled-element hidden md:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search patterns, projects..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-80 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Lock className="h-3 w-3 text-gray-400" />
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Upgrade Notice - Hidden on mobile */}
            
            {/* Mobile Search Button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
              disabled
            >
              <Search className="h-4 w-4" />
            </Button>

            {/* User Menu */}
            <Button variant="ghost" size="sm" disabled className="flex items-center space-x-2 disabled-element">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-gray-600" />
              </div>
              <ChevronDown className="h-4 w-4 text-gray-400 hidden sm:block" />
              <Lock className="h-3 w-3 text-gray-400 hidden sm:block" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Search Bar */}
      {isMobileSearchOpen && (
        <div className="bg-white border-b border-gray-200 px-4 py-3 md:hidden">
          <div className="relative disabled-element">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search patterns, projects..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              disabled
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Lock className="h-3 w-3 text-gray-400" />
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 min-h-screen transition-transform duration-300 ease-in-out
        `}>
          <div className="p-4">
            <div className="space-y-1">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.label}
                    disabled={item.disabled}
                    className={`
                      w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors
                      ${item.active 
                        ? 'bg-primary/10 text-primary border border-primary/20' 
                        : item.disabled 
                          ? 'text-gray-400 cursor-not-allowed hover:bg-gray-50 disabled-element' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                    {item.disabled && <Lock className="h-3 w-3 ml-auto text-gray-400" />}
                  </button>
                );
              })}
            </div>

            {/* Project Stats - Disabled */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200 relative disabled-element">
              <div className="absolute top-2 right-2">
                <Lock className="h-3 w-3 text-gray-400" />
              </div>
              <h3 className="font-medium text-gray-900 mb-2">Project Stats</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Active Projects</span>
                  <span className="blur-sm">12</span>
                </div>
                <div className="flex justify-between">
                  <span>Patterns Used</span>
                  <span className="blur-sm">45</span>
                </div>
                <div className="flex justify-between">
                  <span>Team Members</span>
                  <span className="blur-sm">8</span>
                </div>
              </div>
            </div>

            {/* Help Section */}
            <div className="mt-4">
              <Button variant="ghost" size="sm" disabled className="w-full justify-start disabled-element">
                <HelpCircle className="h-4 w-4 mr-2" />
                Help & Support
                <Lock className="h-3 w-3 ml-auto text-gray-400" />
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-0 p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
} 